import type {
  ContractLanguagePack,
  ProposalTemplateConfig,
} from '@/services/settings';

export function newLanguagePack(
  partial?: Partial<ContractLanguagePack>,
): ContractLanguagePack {
  return {
    id: crypto.randomUUID(),
    name: 'New pack',
    isDefault: false,
    matchStates: [],
    contractTermsHtml: '',
    limitedWarrantyHtml: '',
    ...partial,
  };
}

export function parseMatchStates(raw: string): string[] {
  return Array.from(
    new Set(
      raw
        .split(/[,\s]+/)
        .map((part) => part.trim().toUpperCase())
        .filter((part) => /^[A-Z]{2}$/.test(part)),
    ),
  );
}

export function packsFromTemplate(
  data: Partial<ProposalTemplateConfig> | null | undefined,
): ContractLanguagePack[] {
  const incoming = Array.isArray(data?.languagePacks) ? data!.languagePacks! : [];
  const normalized = incoming
    .filter((pack) => pack && typeof pack === 'object')
    .map((pack, index) =>
      newLanguagePack({
        id: pack.id || `pack-${index + 1}`,
        name: pack.name?.trim() || `Pack ${index + 1}`,
        isDefault: !!pack.isDefault,
        matchStates: Array.isArray(pack.matchStates)
          ? parseMatchStates(pack.matchStates.join(','))
          : [],
        contractTermsHtml: pack.contractTermsHtml || '',
        limitedWarrantyHtml: pack.limitedWarrantyHtml || '',
        contractTermsClauseConfig: pack.contractTermsClauseConfig,
        limitedWarrantyClauseConfig: pack.limitedWarrantyClauseConfig,
      }),
    );
  if (normalized.length > 0) {
    if (!normalized.some((pack) => pack.isDefault)) {
      normalized[0] = { ...normalized[0], isDefault: true };
    }
    return normalized;
  }
  return [
    newLanguagePack({
      id: 'legacy-default',
      name: 'Company default',
      isDefault: true,
      contractTermsHtml: data?.contractTermsHtml || '',
      limitedWarrantyHtml: data?.limitedWarrantyHtml || '',
      contractTermsClauseConfig: data?.contractTermsClauseConfig,
      limitedWarrantyClauseConfig: data?.limitedWarrantyClauseConfig,
    }),
  ];
}

export function defaultLanguagePack(
  packs: ContractLanguagePack[],
): ContractLanguagePack {
  return packs.find((pack) => pack.isDefault) ?? packs[0];
}

export function locationPackId(locationId: string): string {
  return `location:${locationId}`;
}

export function packsAlignedToLocations(
  packs: ContractLanguagePack[],
  locations: LanguageLocation[],
): boolean {
  if (locations.length === 0) return true;
  return (
    locations.length === packs.length &&
    locations.every((location) =>
      packs.some((pack) => pack.id === locationPackId(location.id)),
    )
  );
}

export type LanguageLocation = {
  id: string;
  name: string;
  state?: string | null;
  isHeadquarters?: boolean;
};

/**
 * One pack per company location. Existing copy is reused when a pack
 * already matches the location id, name, or state.
 */
export function alignPacksToLocations(
  packs: ContractLanguagePack[],
  locations: LanguageLocation[],
): ContractLanguagePack[] {
  if (locations.length === 0) return packs;

  const unused = [...packs];
  const fallback = defaultLanguagePack(packs);
  const next: ContractLanguagePack[] = locations.map((location, index) => {
    const keyed = locationPackId(location.id);
    const state = location.state ? parseMatchStates(location.state)[0] : undefined;
    const matchIndex = unused.findIndex((pack) => {
      if (pack.id === keyed || pack.id === location.id) return true;
      if (pack.name.trim().toLowerCase() === location.name.trim().toLowerCase()) {
        return true;
      }
      // Already-keyed location packs keep their own state list. Do not
      // steal a pack because the user added another job-site state.
      if (pack.id.startsWith('location:')) return false;
      return !!state && pack.matchStates.includes(state);
    });
    const source =
      matchIndex >= 0 ? unused.splice(matchIndex, 1)[0] : fallback;
    const reusedExisting = matchIndex >= 0 && (source?.matchStates?.length ?? 0) > 0;
    const matchStates = reusedExisting
      ? source!.matchStates
      : Array.from(new Set([...(source?.matchStates ?? []), ...(state ? [state] : [])]));
    return newLanguagePack({
      ...(source ?? {}),
      id: keyed,
      name: location.name,
      matchStates,
      isDefault: location.isHeadquarters || (index === 0 && !locations.some((row) => row.isHeadquarters)),
    });
  });

  if (!next.some((pack) => pack.isDefault) && next[0]) {
    next[0] = { ...next[0], isDefault: true };
  }
  return next;
}

/**
 * Write new default-pack contract terms onto a proposal-template payload
 * so Settings (future contracts) stay in sync with an estimate-page edit.
 */
export function applyDefaultContractTerms(
  template: Partial<ProposalTemplateConfig> | null | undefined,
  contractTermsHtml: string,
): Partial<ProposalTemplateConfig> {
  const packs = packsFromTemplate(template);
  const fallback = defaultLanguagePack(packs);
  return {
    ...(template ?? {}),
    languagePacks: packs.map((pack) =>
      pack.id === fallback.id ? { ...pack, contractTermsHtml } : pack,
    ),
    contractTermsHtml,
    contractTermsClauseConfig:
      fallback?.contractTermsClauseConfig || template?.contractTermsClauseConfig,
  };
}
